'use client'

import { Check, Phone } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { OnboardingLayout } from '@/features/onboarding'
import {
  CASE_STATUS_PIPELINE_BY_FLOW,
  FlowBadge,
  SERVICE_TYPE_TO_FLOW,
  type FlowType,
} from '@/features/cases'
import { usePublicCaseTracking } from '@/features/public/hooks/use-public'
import { formatDateTime } from '@/lib/format/date'
import { cn } from '@/lib/utils'

const SUBTITLES: Record<FlowType, string> = {
  audit: 'Your portfolio audit is moving through review.',
  trades: 'Your trading activity review is moving through review.',
  build: 'Your portfolio plan is moving through review.',
}

/**
 * The public, token-driven twin of `review-status-view.tsx` — same pipeline visuals (shared via
 * `CASE_STATUS_PIPELINE_BY_FLOW`), but reads from the unauthenticated `PublicCaseTracking` DTO
 * instead of a signed-in investor's real case object. Server-prefetched once by
 * `app/status/[trackingToken]/page.tsx`; an invalid token never reaches this component at all
 * (the page calls `notFound()` before rendering it).
 */
export function StatusView({ trackingToken }: { trackingToken: string }) {
  const { data, isLoading } = usePublicCaseTracking(trackingToken)

  if (isLoading || !data) {
    return (
      <OnboardingLayout eyebrow="Booked" title="Thanks — you're all set" fitContent>
        <Skeleton className="h-40 w-full" />
      </OnboardingLayout>
    )
  }

  const flow: FlowType = SERVICE_TYPE_TO_FLOW[data.serviceType]
  const pipeline = CASE_STATUS_PIPELINE_BY_FLOW[flow]
  const currentIndex = pipeline.findIndex((p) => p.key === data.caseStatus)

  return (
    <OnboardingLayout
      eyebrow="Booked"
      title="Thanks — you're all set"
      description={SUBTITLES[flow]}
      fitContent
    >
      <div className="bg-muted/40 mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          <FlowBadge flow={flow} />
          <span className="text-muted-foreground text-xs font-medium">{data.reference}</span>
          {data.advisorFirstName && (
            <span className="text-muted-foreground text-xs font-medium">
              Advisor: {data.advisorFirstName}
            </span>
          )}
        </div>
      </div>

      <ol className="relative">
        {pipeline.map((step, i) => {
          const isLast = i === pipeline.length - 1
          // The current status is an *achieved* milestone, so it reads as done/checked immediately
          // — same semantics as client-detail.tsx's StatusTimeline and review-status-view.tsx.
          // "Up next" highlights the step after the current one, not the current status itself.
          const done = i <= currentIndex
          const current = i === currentIndex + 1
          return (
            <li key={step.key} className="relative flex items-start gap-3 pb-4 last:pb-0">
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute left-[11px] top-6 h-[calc(100%-8px)] w-px',
                    done ? 'bg-primary' : 'bg-border',
                  )}
                />
              )}
              <span
                className={cn(
                  'ring-card relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ring-4',
                  done && 'bg-primary text-primary-foreground',
                  current && 'border-primary bg-card text-primary border-2',
                  !done && !current && 'bg-muted text-muted-foreground',
                )}
              >
                {done ? <Check className="size-3" strokeWidth={3} /> : i + 1}
              </span>

              {current ? (
                <div className="bg-primary/5 -mt-1 flex flex-1 items-center justify-between gap-2 rounded-lg px-3 py-2">
                  <span className="text-primary text-sm font-semibold">{step.label}</span>
                  <Badge variant="secondary">Up next</Badge>
                </div>
              ) : (
                <span
                  className={cn('pt-0.5 text-sm', done ? 'font-medium' : 'text-muted-foreground')}
                >
                  {step.label}
                </span>
              )}
            </li>
          )
        })}
      </ol>

      <div className="bg-muted mt-4 flex items-center gap-2.5 rounded-lg px-3.5 py-2.5">
        <span className="bg-card text-primary flex size-8 shrink-0 items-center justify-center rounded-full shadow-sm">
          <Phone className="size-4" />
        </span>
        <p className="text-muted-foreground text-xs leading-snug">
          {data.consultationSlotAt
            ? `Nothing to do for you now, ${data.advisorFirstName ? `Advisor ${data.advisorFirstName}` : 'your advisor'} will call you at ${formatDateTime(data.consultationSlotAt)}.`
            : 'Your advisor will be in touch to schedule a time.'}
        </p>
      </div>
    </OnboardingLayout>
  )
}
